import { getPassColor } from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";
import { routePartsManager } from "@/setup/routes/report.routes";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Grid2,
	Tooltip,
	Typography,
} from "@mui/material";
import { Carrier } from "../models/Carrier";
import { assemblyStatusCodes } from "./CarrierAssemblyStatusTag";

const getTooltipText = (carrier: Carrier, position: "top" | "bottom") => {
	if (position == "bottom") {
		return carrier?.BottomRollSelectedPassNumber
			? `Pass ${carrier.BottomRollSelectedPassNumber} with status ${carrier.BottomRollSelectedPassStatus == "R" ? "ready" : "damaged"}`
			: "";
	}

	return carrier?.TopRollSelectedPassNumber
		? `Pass ${carrier.TopRollSelectedPassNumber} with status ${carrier.TopRollSelectedPassStatus == "R" ? "ready" : "damaged"}`
		: "";
};

export const CarrierCard = ({ carrier }: { carrier: Carrier | null }) => {
	const status = assemblyStatusCodes.find(
		(s) => s.Code === carrier?.CarrierStatusCode,
	);

	return (
		<Card
			style={{
				border: "2px solid #FAFAFA",
				borderRadius: 10,
				padding: 5,
				width: "100%",
				marginBottom: 5,
			}}
			elevation={2}
		>
			<CardContent>
				<Box
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					mb={1}
				>
					<Box display="flex" flexDirection="column">
						<Typography variant="caption" color="textSecondary" mb={-0.6}>
							Carrier
						</Typography>
						<Typography variant="h5" fontWeight="bold">
							{carrier?.PartName}
						</Typography>
					</Box>
					<Chip
						label={status?.Name}
						size="small"
						sx={{
							backgroundColor: status?.Color,
							color: status?.TextColor,
							fontWeight: "bold",
						}}
					/>
				</Box>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" color="textSecondary">
							Section:
						</Typography>
						<Typography variant="body1">
							{carrier?.SectionName ?? "-"}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" color="textSecondary">
							Stand:
						</Typography>
						<Typography variant="body1">{carrier?.StandName ?? "-"}</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" color="textSecondary">
							Rollset Letter:
						</Typography>
						<Typography variant="body1">
							{carrier?.RollsetLetter ?? "-"}
						</Typography>
					</Grid2>
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" color="textSecondary">
							Top Roll:
						</Typography>
						<Box>
							<Typography
								variant="body1"
								sx={{
									color: "#1976d2",
									cursor: "pointer",
									lineHeight: 1.2,
								}}
								onClick={() => {
									window.open(
										`${routePartsManager}/${carrier?.TopRollId}`,
										"_blank",
									);
								}}
							>
								{carrier?.TopRollName ?? "-"}
							</Typography>
						</Box>
					</Grid2>
					<Grid2 size={{ xs: 4 }}>
						<Typography variant="body2" color="textSecondary">
							Bottom Roll:
						</Typography>
						<Box>
							<Typography
								variant="body1"
								sx={{
									color: "#1976d2",
									cursor: "pointer",
									lineHeight: 1.2,
								}}
								onClick={() => {
									window.open(
										`${routePartsManager}/${carrier?.BottomRollId}`,
										"_blank",
									);
								}}
							>
								{carrier?.BottomRollName ?? "-"}
							</Typography>
						</Box>
					</Grid2>
					{carrier?.TopRollSelectedPassNumber ? (
						<Grid2 size={{ xs: 4 }}>
							<Typography variant="body2" color="textSecondary">
								Selected Pass:
							</Typography>
							<Tooltip title={getTooltipText(carrier!, "bottom")}>
								<Typography
									variant="body1"
									sx={{
										color: (theme) =>
											getPassColor(carrier!.TopRollSelectedPassStatus!, theme)
												.main,
										lineHeight: 1.2,
									}}
									data-test-id={`roll-pass-${carrier?.TopRollSelectedPassNumber}`}
								>
									{carrier?.TopRollSelectedPassNumber
										? `Pass ${carrier.TopRollSelectedPassNumber}`
										: ""}
								</Typography>
							</Tooltip>
						</Grid2>
					) : null}
				</Grid2>
			</CardContent>
		</Card>
	);
};
